import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, Gavel, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useStore } from '../store/useStore';
import { apiClient } from '../lib/apiClient';
import { formatCurrency, formatDate, formatDateTime, isAdminRole } from '../lib/utils';
import Modal from '../components/Modal';
import type { Tender, TenderDecision, TenderSource, TenderStatus } from '../types';

const DECISIONS: { value: TenderDecision; label: string; cls: string }[] = [
  { value: 'uus', label: 'Uus', cls: 'bg-gray-100 text-gray-600' },
  { value: 'osaleme', label: 'Osaleme', cls: 'bg-blue-100 text-blue-700' },
  { value: 'ei_osale', label: 'Ei osale', cls: 'bg-gray-100 text-gray-400' },
  { value: 'pakkumine_esitatud', label: 'Pakkumine esitatud', cls: 'bg-yellow-100 text-yellow-700' },
  { value: 'voidetud', label: 'Võidetud', cls: 'bg-green-100 text-green-700' },
  { value: 'kaotatud', label: 'Kaotatud', cls: 'bg-red-100 text-red-600' },
];
const DECISION_LABEL = Object.fromEntries(DECISIONS.map((d) => [d.value, d.label])) as Record<TenderDecision, string>;
const DECISION_CLS = Object.fromEntries(DECISIONS.map((d) => [d.value, d.cls])) as Record<TenderDecision, string>;

const SOURCE_LABEL: Record<TenderSource, string> = { avahanked: 'avahanked.ee', ted: 'TED', manual: 'käsitsi' };
const WE_BID: TenderDecision[] = ['osaleme', 'pakkumine_esitatud', 'voidetud', 'kaotatud'];
/** The default view: undecided, plus whatever is still in play. */
const ACTIVE_DECISIONS: TenderDecision[] = ['uus', 'osaleme', 'pakkumine_esitatud'];
/** Bids awaiting an outcome stay visible after the deadline passes — they are
 *  only finished once someone records võidetud or kaotatud. */
const AWAITING_RESULT: TenderDecision[] = ['osaleme', 'pakkumine_esitatud'];

const num = (v: number | string | null): number | null => (v == null ? null : Number(v));
const dstr = (v: string | null): string => (v ? v.split('T')[0] : '');

/** Days from today to the deadline; negative once it has passed. */
function daysLeft(deadline: string | null): number | null {
  if (!deadline) return null;
  const d = new Date(dstr(deadline) + 'T23:59:59Z').getTime() - Date.now();
  return Math.ceil(d / 86_400_000);
}

const inputCls = 'px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400';

/** Inline text cell that saves on blur — same feel as the Müük ledger. */
function NoteCell({ value, placeholder, onSave }: { value: string; placeholder: string; onSave: (v: string) => void }) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <input
      value={local}
      placeholder={placeholder}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => { if (local !== value) onSave(local); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className="w-full px-1.5 py-0.5 text-xs bg-transparent border border-transparent rounded hover:border-gray-200 focus:border-brand-400 focus:bg-white focus:outline-none text-gray-600 placeholder:text-gray-300"
    />
  );
}

function MoneyCell({ value, onSave }: { value: number | null; onSave: (v: number | null) => void }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState('');
  if (editing) {
    return (
      <input
        autoFocus
        type="number"
        min={0}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => { setEditing(false); const n = local === '' ? null : Number(local); if (n !== value) onSave(n); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        className={`${inputCls} w-24 text-right`}
      />
    );
  }
  return (
    <button
      onClick={() => { setLocal(value == null ? '' : String(value)); setEditing(true); }}
      className="w-full text-right text-xs text-gray-700 hover:text-brand-600 tabular-nums"
      title="Meie pakkumise summa"
    >
      {value == null ? <span className="text-gray-300">—</span> : formatCurrency(value)}
    </button>
  );
}

export default function Tenders() {
  const currentUser = useStore((s) => s.currentUser);
  const users = useStore((s) => s.users);
  const projects = useStore((s) => s.projects);

  const [tenders, setTenders] = useState<Tender[]>([]);
  const [status, setStatus] = useState<TenderStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  // Default to the tenders that still need something from us; the ones we
  // declined (and concluded ones) only clutter the list until asked for.
  const [decisionFilter, setDecisionFilter] = useState<'active' | 'all' | TenderDecision>('active');
  const [sourceFilter, setSourceFilter] = useState<'all' | TenderSource>('all');
  const [showClosed, setShowClosed] = useState(false);
  const [search, setSearch] = useState('');

  const isAdmin = isAdminRole(currentUser?.role);
  const canUse = currentUser && ['super_admin', 'admin', 'sales', 'sales_pm'].includes(currentUser.role);

  const load = useCallback(async () => {
    try {
      setError('');
      const r = await apiClient.get<{ tenders: Tender[]; status: TenderStatus }>('/tenders');
      setTenders(r.tenders);
      setStatus(r.status);
    } catch (e) {
      setError((e as Error)?.message ?? 'Hangete laadimine ebaõnnestus.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id: string, updates: Partial<Tender>) => {
    setTenders((prev) => prev.map((t) => (t.id === id ? { ...t, ...updates } : t))); // optimistic
    try {
      const r = await apiClient.patch<{ tender: Tender }>(`/tenders/${id}`, updates);
      setTenders((prev) => prev.map((t) => (t.id === r.tender.id ? r.tender : t)));
      setStatus((s) => (s ? { ...s, undecided: s.undecided + (updates.decision === 'uus' ? 1 : updates.decision ? -1 : 0) } : s));
    } catch (e) {
      useStore.getState().addError('updateTender', e);
      load();
    }
  };

  const remove = async (t: Tender) => {
    if (!window.confirm(`Kustutada käsitsi lisatud hange "${t.title}"?`)) return;
    try {
      await apiClient.delete(`/tenders/${t.id}`);
      setTenders((prev) => prev.filter((x) => x.id !== t.id));
    } catch (e) {
      useStore.getState().addError('deleteTender', e);
    }
  };

  const sync = async () => {
    setSyncing(true);
    setError('');
    try {
      await apiClient.post('/tenders/sync', {});
      await load();
    } catch (e) {
      setError((e as Error)?.message ?? 'Sünkroonimine ebaõnnestus.');
    } finally {
      setSyncing(false);
    }
  };

  // Salespeople: project sales owners plus anyone already on a tender.
  const assignees = useMemo(() => {
    const ids = new Set(projects.map((p) => p.salesOwnerId));
    for (const t of tenders) if (t.assigneeId) ids.add(t.assigneeId);
    return users
      .filter((u) => ids.has(u.id) && !(u.name === 'Admin' || u.email === 'admin@vdisain.ee'))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projects, tenders, users]);
  const userName = (id: string | null) => users.find((u) => u.id === id)?.name ?? '';

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tenders.filter((t) => {
      if (!showClosed && !t.isOpen && !AWAITING_RESULT.includes(t.decision)) return false;
      if (decisionFilter === 'active') {
        if (!ACTIVE_DECISIONS.includes(t.decision)) return false;
      } else if (decisionFilter !== 'all' && t.decision !== decisionFilter) return false;
      if (sourceFilter !== 'all' && t.source !== sourceFilter) return false;
      if (q && !`${t.title} ${t.buyerName} ${t.cpvCode} ${t.cpvLabel}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [tenders, showClosed, decisionFilter, sourceFilter, search]);

  // How many rows the default view leaves out, so the count can say so.
  const hiddenByDefault = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tenders.filter((t) => {
      if (!showClosed && !t.isOpen && !AWAITING_RESULT.includes(t.decision)) return false;
      if (sourceFilter !== 'all' && t.source !== sourceFilter) return false;
      if (q && !`${t.title} ${t.buyerName} ${t.cpvCode} ${t.cpvLabel}`.toLowerCase().includes(q)) return false;
      return !ACTIVE_DECISIONS.includes(t.decision);
    }).length;
  }, [tenders, showClosed, sourceFilter, search]);

  const stats = useMemo(() => {
    const by = (d: TenderDecision) => tenders.filter((t) => t.decision === d).length;
    const won = by('voidetud');
    const lost = by('kaotatud');
    return {
      open: tenders.filter((t) => t.isOpen).length,
      undecided: tenders.filter((t) => t.isOpen && t.decision === 'uus').length,
      bidding: by('osaleme') + by('pakkumine_esitatud'),
      won,
      lost,
      winRate: won + lost > 0 ? Math.round((won / (won + lost)) * 100) : null,
    };
  }, [tenders]);

  if (!canUse) {
    return <div className="p-8 text-center text-gray-400">See leht on nähtav adminidele ja müügiinimestele.</div>;
  }

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hanked</h1>
          <p className="text-gray-400 text-sm mt-0.5">
            Veebi-, tarkvara- ja disainihanked · avahanked.ee + TED
            {status?.lastSync && <> · sünkroonitud {formatDateTime(status.lastSync)}</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={sync}
              disabled={syncing}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Sünkroonin…' : 'Sünkrooni'}
            </button>
          )}
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600"
          >
            <Plus size={15} /> Lisa hange
          </button>
        </div>
      </div>

      {error && <p className="mb-4 text-sm text-red-500">{error}</p>}
      {status?.syncError && <p className="mb-4 text-xs text-amber-600">Viimane sünk: {status.syncError}</p>}

      {/* Where we stand */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {[
          { label: 'Avatud', value: String(stats.open), note: 'hanget vastamas' },
          { label: 'Otsustamata', value: String(stats.undecided), note: 'vajab otsust', tone: stats.undecided > 0 ? 'text-brand-600' : 'text-gray-900' },
          { label: 'Osaleme', value: String(stats.bidding), note: 'töös või esitatud' },
          { label: 'Võidetud', value: String(stats.won), note: `kaotatud ${stats.lost}`, tone: 'text-green-600' },
          { label: 'Võiduprotsent', value: stats.winRate == null ? '—' : `${stats.winRate}%`, note: 'esitatud pakkumistest' },
        ].map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-xs font-medium text-gray-400">{c.label}</p>
            <p className={`text-2xl font-bold mt-0.5 ${c.tone ?? 'text-gray-900'}`}>{c.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{c.note}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Otsi hanget, hankijat, CPV-d…"
            className={`${inputCls} pl-8 w-64`}
          />
        </div>
        <select value={decisionFilter} onChange={(e) => setDecisionFilter(e.target.value as 'active' | 'all' | TenderDecision)} className={inputCls}>
          <option value="active">Uued ja osaleme</option>
          <option value="all">Kõik otsused</option>
          {DECISIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value as 'all' | TenderSource)} className={inputCls}>
          <option value="all">Kõik allikad</option>
          <option value="avahanked">avahanked.ee</option>
          <option value="ted">TED</option>
          <option value="manual">Käsitsi lisatud</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
          <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} className="rounded border-gray-300" />
          Näita ka lõppenud (ajalugu)
        </label>
        <span className="text-xs text-gray-400 ml-auto">
          {rows.length} hanget
          {decisionFilter === 'active' && hiddenByDefault > 0 && (
            <>
              <span className="text-gray-300"> · {hiddenByDefault} peidetud</span>
              <button onClick={() => setDecisionFilter('all')} className="ml-1.5 text-brand-500 hover:text-brand-700 font-medium">
                näita kõiki
              </button>
            </>
          )}
        </span>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 py-10 text-center">Laen…</p>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <Gavel size={36} className="mx-auto mb-3 text-gray-200" />
          <p className="text-sm text-gray-400">Ühtegi hanget ei leitud. {!showClosed && 'Proovi näidata ka lõppenuid.'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left">
                {['Tähtaeg', 'Hankija', 'Hange', 'Eeldatav maksumus', 'Otsus', 'Vastutaja', 'Meie pakkumine', 'Märkus', ''].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-xs font-medium text-gray-400 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((t) => {
                const left = daysLeft(t.deadlineAt);
                const urgent = t.isOpen && left != null && left <= 3;
                return (
                  <tr key={t.id} className={`hover:bg-gray-50/70 ${t.isOpen ? '' : 'opacity-55'}`}>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <p className="text-xs font-medium text-gray-800">{t.deadlineAt ? formatDate(t.deadlineAt) : '—'}</p>
                      <p className={`text-[11px] ${urgent ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                        {!t.isOpen ? 'lõppenud' : left == null ? 'tähtaeg teadmata' : left < 0 ? 'tähtaeg möödas' : `${left} päeva`}
                      </p>
                    </td>
                    <td className="px-3 py-2.5 max-w-[190px]">
                      <p className="text-xs text-gray-700 truncate" title={t.buyerName}>{t.buyerName || '—'}</p>
                      <p className="text-[11px] text-gray-300">{SOURCE_LABEL[t.source]}</p>
                    </td>
                    <td className="px-3 py-2.5 max-w-[330px]">
                      {t.registerUrl ? (
                        <a
                          href={t.registerUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium text-gray-900 hover:text-brand-600 inline-flex items-start gap-1"
                          title={t.title}
                        >
                          <span className="line-clamp-2">{t.title}</span>
                          <ExternalLink size={11} className="shrink-0 mt-0.5 text-gray-300" />
                        </a>
                      ) : (
                        <p className="text-xs font-medium text-gray-900 line-clamp-2" title={t.title}>{t.title}</p>
                      )}
                      <p className="text-[11px] text-gray-400 mt-0.5" title={t.cpvLabel}>
                        {t.cpvCode}{t.cpvLabel ? ` · ${t.cpvLabel}` : ''}
                      </p>
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs text-gray-700 whitespace-nowrap tabular-nums">
                      {num(t.estimatedValue) == null ? <span className="text-gray-300">—</span> : formatCurrency(num(t.estimatedValue) as number)}
                    </td>
                    <td className="px-3 py-2.5">
                      <select
                        value={t.decision}
                        onChange={(e) => patch(t.id, { decision: e.target.value as TenderDecision })}
                        className={`text-xs font-medium rounded-full pl-2.5 pr-6 py-1 border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-400 appearance-none ${DECISION_CLS[t.decision]}`}
                        title={t.decidedAt ? `${DECISION_LABEL[t.decision]} · ${userName(t.decidedById)} · ${formatDate(t.decidedAt)}` : 'Määra otsus'}
                      >
                        {DECISIONS.map((d) => <option key={d.value} value={d.value} className="bg-white text-gray-700">{d.label}</option>)}
                      </select>
                      {t.decidedAt && (
                        <p className="text-[10px] text-gray-300 mt-0.5 whitespace-nowrap">
                          {userName(t.decidedById)} · {formatDate(t.decidedAt)}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <select
                        value={t.assigneeId ?? ''}
                        onChange={(e) => patch(t.id, { assigneeId: e.target.value || null })}
                        className="text-xs text-gray-600 bg-transparent border border-transparent rounded-lg hover:border-gray-200 focus:border-brand-400 py-1 pl-1 pr-5 focus:outline-none cursor-pointer"
                      >
                        <option value="">—</option>
                        {assignees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2.5 w-28">
                      {WE_BID.includes(t.decision)
                        ? <MoneyCell value={num(t.ourOfferValue)} onSave={(v) => patch(t.id, { ourOfferValue: v })} />
                        : <span className="block text-right text-xs text-gray-200">—</span>}
                    </td>
                    <td className="px-3 py-2.5 min-w-[160px]">
                      <NoteCell
                        value={t.decisionNote}
                        placeholder={t.decision === 'ei_osale' ? 'miks ei osale?' : 'märkus'}
                        onSave={(v) => patch(t.id, { decisionNote: v })}
                      />
                    </td>
                    <td className="px-2 py-2.5">
                      {t.source === 'manual' && (
                        <button onClick={() => remove(t)} className="p-1 text-gray-300 hover:text-red-500 rounded" title="Kustuta">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-4">
        Sünkroonitakse kord ööpäevas. Imporditakse ainult CPV-koodid {status?.cpvPrefixes.join(', ') ?? '722, 724, 79822'}xxx —
        tarkvaraarendus, veebiteenused ja graafiline disain. Lõppenud hanked jäävad ajalukku alles.
      </p>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Lisa hange käsitsi" size="md">
        <AddTenderForm
          assignees={assignees}
          onDone={async () => { setShowAdd(false); await load(); }}
          onCancel={() => setShowAdd(false)}
        />
      </Modal>
    </div>
  );
}

function AddTenderForm({
  assignees,
  onDone,
  onCancel,
}: {
  assignees: { id: string; name: string }[];
  onDone: () => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    title: '',
    buyerName: '',
    registerUrl: '',
    cpvCode: '',
    publishedAt: '',
    deadlineAt: '',
    estimatedValue: '',
    decision: 'uus' as TenderDecision,
    assigneeId: '',
    decisionNote: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    setErr('');
    try {
      await apiClient.post('/tenders', {
        title: form.title.trim(),
        buyerName: form.buyerName.trim(),
        registerUrl: form.registerUrl.trim(),
        cpvCode: form.cpvCode.trim(),
        publishedAt: form.publishedAt || null,
        deadlineAt: form.deadlineAt || null,
        estimatedValue: form.estimatedValue === '' ? null : Number(form.estimatedValue),
        decision: form.decision,
        assigneeId: form.assigneeId || null,
        decisionNote: form.decisionNote.trim(),
      });
      await onDone();
    } catch (e) {
      setErr((e as Error)?.message ?? 'Salvestamine ebaõnnestus.');
    } finally {
      setSaving(false);
    }
  };

  const field = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500';
  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs text-gray-500 block mb-1">Hanke nimi *</label>
        <input value={form.title} onChange={(e) => set('title', e.target.value)} className={field} placeholder="nt Veebilehe arendus- ja hooldustööd" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Hankija</label>
          <input value={form.buyerName} onChange={(e) => set('buyerName', e.target.value)} className={field} />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">CPV</label>
          <input value={form.cpvCode} onChange={(e) => set('cpvCode', e.target.value)} className={field} placeholder="72413000" />
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Link registrisse</label>
        <input value={form.registerUrl} onChange={(e) => set('registerUrl', e.target.value)} className={field} placeholder="https://riigihanked.riik.ee/…" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Avaldatud</label>
          <input type="date" value={form.publishedAt} onChange={(e) => set('publishedAt', e.target.value)} className={field} />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Tähtaeg</label>
          <input type="date" value={form.deadlineAt} onChange={(e) => set('deadlineAt', e.target.value)} className={field} />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Eeldatav maksumus (€)</label>
          <input type="number" min={0} value={form.estimatedValue} onChange={(e) => set('estimatedValue', e.target.value)} className={field} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Otsus</label>
          <select value={form.decision} onChange={(e) => set('decision', e.target.value as TenderDecision)} className={field}>
            {DECISIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Vastutaja</label>
          <select value={form.assigneeId} onChange={(e) => set('assigneeId', e.target.value)} className={field}>
            <option value="">—</option>
            {assignees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Märkus</label>
        <input value={form.decisionNote} onChange={(e) => set('decisionNote', e.target.value)} className={field} />
      </div>
      {err && <p className="text-xs text-red-500">{err}</p>}
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Tühista</button>
        <button
          onClick={save}
          disabled={saving || !form.title.trim()}
          className="px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium disabled:opacity-50"
        >
          {saving ? 'Salvestan…' : 'Lisa hange'}
        </button>
      </div>
    </div>
  );
}
